import { buildSliderConfirmedAgentMessage } from '@fruitful/llm-service-shared';
import {
    type SliderInputUnit,
    type SliderInputWidgetProps,
} from '@fruitful/types';
import React, { useCallback, useState } from 'react';
import {
    Button,
    ButtonSize,
    ButtonVariant,
    H6Regular,
    Input,
    SlideRulePicker,
    XStack,
    YStack,
} from 'smoothie';

import { CHAT_INPUT_WIDGET } from './shared-chat/ChatLayout';
import { BaseInputWidget } from './widgets/BaseInputWidget';

interface SliderInputWidgetComponentProps extends SliderInputWidgetProps {
    disabled?: boolean;
    isActive?: boolean;
    variant?: 'inline' | 'footer';
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

const formatValue = (value: number, unit: SliderInputUnit): string => {
    const rounded = Math.round(value);
    return unit === 'currency'
        ? `$${rounded.toLocaleString()}`
        : rounded.toLocaleString();
};

const clamp = (value: number, min: number, max: number): number =>
    Math.min(Math.max(value, min), max);

export const SliderInputWidget = ({
    responseKey,
    promptText,
    min,
    max,
    step = 1,
    initialValue,
    unit = 'currency',
    allowManualEntry = false,
    disabled = false,
    isActive = true,
    variant = 'inline',
    onSubmit,
}: SliderInputWidgetComponentProps) => {
    const start = clamp(initialValue ?? min, min, max);
    const [value, setValue] = useState<number>(start);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isManualMode, setIsManualMode] = useState(false);
    const [manualText, setManualText] = useState('');

    const isWidgetDisabled = disabled || !isActive || hasSubmitted;
    const isFooterVariant = variant === 'footer';

    const textFormatter = useCallback(
        (raw: string) => formatValue(Number(raw), unit),
        [unit],
    );

    const openManualEntry = useCallback(() => {
        if (isWidgetDisabled) return;
        setManualText(String(Math.round(value)));
        setIsManualMode(true);
    }, [isWidgetDisabled, value]);

    const cancelManualEntry = useCallback(() => {
        setIsManualMode(false);
    }, []);

    const submitManualEntry = useCallback(() => {
        if (isWidgetDisabled) {
            return;
        }
        const cleaned = manualText.replace(/[^0-9.]/g, '');
        const parsed = cleaned === '' ? NaN : Number(cleaned);
        // Empty / unparseable input falls back to the current value rather than
        // snapping to 0. A typed amount is taken exactly as entered (clamped).
        const next = Number.isNaN(parsed) ? value : clamp(parsed, min, max);
        setValue(next);
        setHasSubmitted(true);
        const displayText = formatValue(next, unit);
        const agentPayload = buildSliderConfirmedAgentMessage({
            displayText,
            payload: { responseKey, value: next },
        });
        void onSubmit(displayText, agentPayload);
    }, [
        isWidgetDisabled,
        manualText,
        value,
        min,
        max,
        unit,
        responseKey,
        onSubmit,
    ]);

    const handleSend = useCallback(() => {
        if (isWidgetDisabled) {
            return;
        }
        const displayText = formatValue(value, unit);
        const agentPayload = buildSliderConfirmedAgentMessage({
            displayText,
            payload: { responseKey, value },
        });
        setHasSubmitted(true);
        void onSubmit(displayText, agentPayload);
    }, [isWidgetDisabled, value, unit, responseKey, onSubmit]);

    return (
        <BaseInputWidget
            alignSelf={isFooterVariant ? 'stretch' : 'center'}
            width="100%"
            maxWidth={isFooterVariant ? '100%' : CHAT_INPUT_WIDGET.maxWidth}
            paddingTop={CHAT_INPUT_WIDGET.paddingTop}
            paddingBottom={12}
        >
            <YStack
                gap={12}
                paddingHorizontal={CHAT_INPUT_WIDGET.rowPaddingHorizontal}
            >
                {promptText != null && (
                    <H6Regular paddingBottom={12}>{promptText}</H6Regular>
                )}

                {isManualMode ? (
                    <YStack gap={12}>
                        <Input
                            value={manualText}
                            onChangeText={setManualText}
                            keyboardType="numeric"
                            placeholder="Enter an exact amount"
                            onSubmitEditing={submitManualEntry}
                            returnKeyType="done"
                        />
                        <XStack gap={8}>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Cancel"
                                    variant={ButtonVariant.SECONDARY}
                                    size={ButtonSize.LARGE}
                                    onPress={cancelManualEntry}
                                />
                            </XStack>
                            <XStack flex={1}>
                                <Button
                                    width="full"
                                    text="Set"
                                    variant={ButtonVariant.PRIMARY}
                                    size={ButtonSize.LARGE}
                                    disabled={isWidgetDisabled}
                                    onPress={submitManualEntry}
                                />
                            </XStack>
                        </XStack>
                    </YStack>
                ) : (
                    <YStack
                        marginHorizontal={
                            -CHAT_INPUT_WIDGET.rowPaddingHorizontal
                        }
                        gap={24}
                    >
                        <SlideRulePicker
                            min={min}
                            max={max}
                            step={step}
                            initialValue={value}
                            indicatorHeight={60}
                            shortStepHeight={24}
                            longStepHeight={38}
                            verticalGap={10}
                            // Match the Figma: all ticks (short and decade) are
                            // a single uniform gray, not two-tone.
                            shortStepColor="$gray900"
                            longStepColor="$gray900"
                            textFormatter={textFormatter}
                            onValueChangeEnd={(next) => {
                                setValue(next);
                            }}
                            onEditPress={
                                allowManualEntry ? openManualEntry : undefined
                            }
                        />
                        <Button
                            text="Next"
                            variant={ButtonVariant.PRIMARY}
                            size={ButtonSize.LARGE}
                            width="full"
                            disabled={isWidgetDisabled}
                            onPress={handleSend}
                        />
                    </YStack>
                )}
            </YStack>
        </BaseInputWidget>
    );
};
